import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Headphones, Shield, Users, Ticket, MessageCircle, Bot, 
  Mail, Phone, CheckCircle, ArrowRight, BarChart3, Building2,
  Clock, Zap, Globe, Lock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function LandingPage() {
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '', message: '' });
  const [demoSent, setDemoSent] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/public/ticket`, {
        name: demoForm.name,
        email: demoForm.email,
        phone: '',
        subject: `Richiesta Demo - ${demoForm.company}`,
        description: `Azienda: ${demoForm.company}\n\n${demoForm.message}`,
        category: 'general',
        priority: 'medium'
      });
      setDemoSent(true);
    } catch (e) {
      setDemoSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#002FA7] rounded-lg flex items-center justify-center">
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>IKONET Solutions</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-gray-600 hover:text-[#002FA7] transition">Funzionalità</a>
            <a href="#pricing" className="text-gray-600 hover:text-[#002FA7] transition">Piani</a>
            <a href="#demo" className="text-gray-600 hover:text-[#002FA7] transition">Demo</a>
          </nav>
          <Link to="/login">
            <Button variant="outline" className="border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white">
              Accedi
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-[#002FA7] px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Potenziato dall'Intelligenza Artificiale
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assistenza IT Intelligente<br />
            <span className="text-[#002FA7]">per Aziende che Crescono</span>
          </h1>
          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Gestisci ticket, tecnici e clienti da un'unica piattaforma. Con agente IA integrato,
            notifiche WhatsApp automatiche e comunicazione in tempo reale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/start">
              <Button className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white h-12 px-8 text-base">
                Inizia Gratis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#demo">
              <Button variant="outline" className="h-12 px-8 text-base border-gray-200">
                Richiedi una Demo
              </Button>
            </a>
          </div>
          <p className="text-sm text-gray-400 mt-4">7 giorni di prova gratuita • Nessuna carta di credito</p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0F172A] py-8">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
          {[
            { value: '99.9%', label: 'Uptime garantito' },
            { value: '<3s', label: 'Tempo risposta IA' },
            { value: '24/7', label: 'Assistente IA attivo' },
            { value: '100%', label: 'Dati separati per azienda' }
          ].map((s, i) => (
            <div key={i}>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-sm text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tutto quello che serve per<br />un supporto IT eccellente
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Ticket, title: 'Gestione Ticket', desc: 'Crea, assegna e traccia ogni richiesta. Priorità, categorie e filtri avanzati.' },
              { icon: Bot, title: 'Agente IA Rebecca', desc: 'Assistente IA che diagnostica problemi, suggerisce soluzioni e risponde a voce.' },
              { icon: MessageCircle, title: 'Chat + WhatsApp', desc: 'Comunicazione bidirezionale con l\'utente via chat, email e WhatsApp automatico.' },
              { icon: Users, title: 'Gestione Tecnici', desc: 'Assegna ticket ai tecnici, monitora disponibilità e carico di lavoro.' },
              { icon: BarChart3, title: 'Timeline Completa', desc: 'Ogni azione registrata: assegnazione, messaggi, email, WhatsApp, cambi stato.' },
              { icon: Building2, title: 'Multi-Azienda', desc: 'Gestisci più clienti con dati completamente separati. Perfetto per MSP e provider IT.' },
              { icon: Mail, title: 'Email Automatiche', desc: 'Notifiche automatiche a utenti e tecnici per ogni evento del ticket.' },
              { icon: Shield, title: 'Controllo Totale', desc: 'Super Admin panel per gestire aziende, utenti, licenze e accessi.' },
              { icon: Lock, title: 'Sicurezza', desc: 'Autenticazione JWT, controllo accessi per ruolo, licenze con scadenza.' }
            ].map((f, i) => (
              <Card key={i} className="border border-gray-200 shadow-none hover:shadow-md hover:border-[#002FA7]/20 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-center mb-16" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Come funziona
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'L\'utente apre il ticket', desc: 'Tramite il form dedicato della tua azienda' },
              { step: '2', title: 'Notifica automatica', desc: 'Email e WhatsApp al tecnico assegnato' },
              { step: '3', title: 'Chat in tempo reale', desc: 'Tecnico e utente comunicano direttamente' },
              { step: '4', title: 'Problema risolto', desc: 'Timeline completa di ogni azione svolta' }
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">{s.step}</div>
                <h3 className="font-medium mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Piani e Prezzi
          </h2>
          <p className="text-center text-gray-600 mb-12">Scegli il piano perfetto per la tua azienda</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Trial', price: 'Gratis', period: '7 giorni', features: ['Fino a 2 tecnici', 'Ticket illimitati', 'Chat + Email', 'Agente IA Rebecca', 'Supporto base'], cta: 'Inizia Gratis', highlight: false },
              { name: 'Standard', price: '49', period: '/mese', features: ['Fino a 10 tecnici', 'Ticket illimitati', 'Chat + Email + WhatsApp', 'Agente IA Rebecca', 'Timeline completa', 'Link supporto dedicato', 'Supporto prioritario'], cta: 'Scegli Standard', highlight: true },
              { name: 'Premium', price: '99', period: '/mese', features: ['Tecnici illimitati', 'Ticket illimitati', 'Tutte le funzionalità Standard', 'Multi-sede', 'API personalizzate', 'SLA garantito', 'Account manager dedicato'], cta: 'Scegli Premium', highlight: false }
            ].map((p, i) => (
              <Card key={i} className={`border ${p.highlight ? 'border-[#002FA7] shadow-lg scale-105' : 'border-gray-200 shadow-none'} relative`}>
                {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white px-4 py-1 rounded-full text-xs font-medium">Più Popolare</div>}
                <CardContent className="p-8">
                  <h3 className="text-xl font-medium mb-2">{p.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    {p.price === 'Gratis' ? (
                      <span className="text-4xl font-bold">Gratis</span>
                    ) : (
                      <><span className="text-4xl font-bold">€{p.price}</span><span className="text-gray-500">{p.period}</span></>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#16A34A] flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/start">
                    <Button className={`w-full ${p.highlight ? 'bg-[#002FA7] text-white' : 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-50'}`}>
                      {p.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Request */}
      <section id="demo" className="py-20 px-4">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-center mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Richiedi una Demo Gratuita
          </h2>
          <p className="text-center text-gray-600 mb-8">Compila il form e ti contatteremo entro 24 ore</p>
          
          {demoSent ? (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-green-800 mb-2">Richiesta Inviata!</h3>
                <p className="text-green-700">Ti contatteremo entro 24 ore per organizzare la demo.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-gray-200">
              <CardContent className="p-8">
                <form onSubmit={handleDemoRequest} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input placeholder="Nome e Cognome" value={demoForm.name} onChange={(e) => setDemoForm({...demoForm, name: e.target.value})} required />
                    <Input placeholder="Email" type="email" value={demoForm.email} onChange={(e) => setDemoForm({...demoForm, email: e.target.value})} required />
                  </div>
                  <Input placeholder="Nome Azienda" value={demoForm.company} onChange={(e) => setDemoForm({...demoForm, company: e.target.value})} required />
                  <Textarea placeholder="Come possiamo aiutarti?" rows={4} value={demoForm.message} onChange={(e) => setDemoForm({...demoForm, message: e.target.value})} />
                  <Button type="submit" className="w-full bg-[#002FA7] text-white h-12 text-base">
                    Richiedi Demo Gratuita
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12 px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#002FA7] rounded-lg flex items-center justify-center">
                <Headphones className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold">IKONET Solutions</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              Piattaforma intelligente per la gestione dell'assistenza IT.
              Potenziata dall'IA per aziende che vogliono crescere.
            </p>
          </div>
          <div>
            <h4 className="font-medium mb-4">Contatti</h4>
            <div className="space-y-2 text-sm text-gray-400">
              <p className="flex items-center gap-2"><Mail className="w-4 h-4" />supporto@ikonetsolutions.com</p>
              <p className="flex items-center gap-2"><Globe className="w-4 h-4" />ikonetsolutions.com</p>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-4">Link Utili</h4>
            <div className="space-y-2 text-sm text-gray-400">
              <Link to="/login" className="block hover:text-white transition">Accedi alla piattaforma</Link>
              <Link to="/support" className="block hover:text-white transition">Richiedi Assistenza</Link>
              <a href="https://offlinepdfstudio.com/" className="block hover:text-white transition">Strumenti PDF gratis e offline</a>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} IKONET Solutions. Tutti i diritti riservati. Powered by IKONET Solutions
        </div>
      </footer>
    </div>
  );
}
